import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
}

const SITE_DOMAIN = ['solariconstrucao', 'com', 'br'].join('.');
const DEFAULT_CANONICAL_URL = `https://${SITE_DOMAIN}`;

const SEOHead = ({
  title = 'Kit Porta Pronta em Goiânia e Região | Solari Soluções para Construção',
  description = 'Kit Porta Pronta com instalação rápida e economia de até 40% na mão de obra. Atendemos construtores em Goiânia, Aparecida de Goiânia e região. Solicite orçamento grátis!',
  keywords = 'kit porta pronta, porta pronta goiânia, porta para construção, porta pronta construtora, kit porta completo, porta com batente, porta com fechadura, portas para obra, solari construção',
  canonicalUrl = DEFAULT_CANONICAL_URL,
  ogImage = '/og-image.jpg',
}: SEOHeadProps) => {
  const contactEmail = ['comercial', ['solariconstrucao', 'com', 'br'].join('.')].join('@');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Solari Soluções para Construção',
    description: 'Especialista em kits porta pronta para construtores em Goiânia e Região Metropolitana.',
    url: canonicalUrl,
    telephone: '+55-62-99909-4131',
    email: contactEmail,
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'GO',
      addressCountry: 'BR',
    },
    areaServed: [
      { '@type': 'City', name: 'Goiânia' },
      { '@type': 'City', name: 'Aparecida de Goiânia' },
      { '@type': 'City', name: 'Senador Canedo' },
      { '@type': 'City', name: 'Anápolis' },
    ],
    priceRange: '$$',
    openingHours: 'Mo-Fr 08:00-18:00',
    sameAs: [
      'https://www.instagram.com/solari.construcao/',
      'https://www.linkedin.com/company/solari-solu%C3%A7%C3%B5es-para-constru%C3%A7%C3%A3o/',
      'https://www.youtube.com/@solarisolucoes',
      'https://www.facebook.com/profile.php?id=61562757667436',
    ],
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'O que é um kit porta pronta?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O kit porta pronta é uma solução completa que inclui folha da porta, batente/portal, alizares/guarnição, fechadura e dobradiças. Todos os itens vêm prontos para instalação.',
        },
      },
      {
        '@type': 'Question',
        name: 'Qual a vantagem de usar kit porta pronta na minha obra?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'As principais vantagens são: instalação rápida (em até 30 minutos), economia de até 40% na mão de obra, eliminação de etapas como pintura e acabamento, produto com garantia de qualidade.',
        },
      },
      {
        '@type': 'Question',
        name: 'Vocês atendem em quais regiões?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Atendemos Goiânia e Região Metropolitana.',
        },
      },
    ],
  };

  return (
    <Helmet>
      {/* Meta tags básicas */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content="Solari Soluções para Construção" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Geo tags */}
      <meta name="geo.region" content="BR-GO" />
      <meta name="geo.placename" content="Goiânia" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
